import { z } from 'zod'
import {
  BusinessType,
  DeliveryTimeframe,
  FulfillmentMethod,
  MccCardScheme
} from 'generated/graphql'
import { stringifiedNumberSchema } from 'utils/custom-schemas'

const entityMccSchema = z
  .object({
    id: z.string(),
    code: z.number(),
    cardSchemas: z.array(z.nativeEnum(MccCardScheme)),
    archived: z.boolean()
  })
  .passthrough()

const businessStructureDetailsBase = z.object({
  businessType: z.nativeEnum(BusinessType).nullish(),
  businessTypeDescription: z.string().nullish()
})

const businessDetailsSchema = z.object({
  industryDescription: z.string().min(1)
})

const businessProcessingInfoBase = z.object({
  numberOfPhysicalLocations: z.coerce
    .number({ invalid_type_error: 'Required' })
    .nullish(),
  applyingForAdditionalLocations: z.boolean().nullish(),
  isWebsitePciCompliant: z.boolean(),
  hasAcceptedCreditCardsBefore: z.boolean(),
  hasAcceptedCcBeforeDescription: z.string().nullish(),
  terminatedByPreviousProcessor: z.boolean(),
  hasExistingPaycomMerchant: z.boolean().nullish(),
  existingPaycomMerchantId: z.string().nullish(),
  returnAndRefundPolicy: z.string().min(1),
  wantsAmexMarketingMaterials: z.boolean()
})

const innerBusinessActivityBase = z.object({
  ecommerceBasisPoints: stringifiedNumberSchema(z.number().min(0).max(100)),
  manuallyKeyedCnpMotoBasisPoints: stringifiedNumberSchema(
    z.number().min(0).max(100)
  ),
  manuallyKeyedCpImprintBasisPoints: stringifiedNumberSchema(
    z.number().min(0).max(100)
  ),
  cardPresentBasisPoints: stringifiedNumberSchema(z.number().min(0).max(100)),
  businessToBusinessBasisPoints: stringifiedNumberSchema(
    z.number().min(0).max(100)
  ),
  monthlyCardVolumeAmount: stringifiedNumberSchema(z.number().min(0)),
  maximumMonthlyCardVolumeAmount: stringifiedNumberSchema(z.number().min(0)),
  averageTransactionAmount: stringifiedNumberSchema(z.number().min(0)),
  maximumTransactionAmount: stringifiedNumberSchema(z.number().min(0)),
  requestingPayoutsToCustomers: z.boolean(),
  annualAmexVolumeExceeds: z.boolean(),
  esaNumber: z.string().nullish(),
  seasonalBusiness: z.boolean(),
  seasonalBusinessMonths: z.array(z.string()).nullish()
})

const fulfillmentDetailsBase = z.object({
  fulfillmentMethod: z.nativeEnum(FulfillmentMethod).nullish(),
  fulfillmentMethodDescription: z.string().nullish(),
  deliveryTimeframe: z.nativeEnum(DeliveryTimeframe).nullish()
})

const baseSchema = z.object({
  creditCardProcessingMethods: z.void().optional(),
  mccCodes: z.array(entityMccSchema),
  businessStructureDetails: businessStructureDetailsBase,
  businessDetails: businessDetailsSchema,
  businessProcessingInfo: businessProcessingInfoBase,
  businessActivity: innerBusinessActivityBase,
  fulfillmentDetails: fulfillmentDetailsBase
})

const crossFieldRefinements = (data: unknown, ctx: z.RefinementCtx): void => {
  const root = (data ?? {}) as {
    businessStructureDetails?: {
      businessType?: BusinessType | null
      businessTypeDescription?: string | null
    }
    businessProcessingInfo?: {
      numberOfPhysicalLocations?: number | string | null
      hasAcceptedCreditCardsBefore?: boolean | null
      hasAcceptedCcBeforeDescription?: string | null
      hasExistingPaycomMerchant?: boolean | null
      existingPaycomMerchantId?: string | null
    }
    businessActivity?: {
      monthlyCardVolumeAmount?: string | null
      maximumMonthlyCardVolumeAmount?: string | null
      averageTransactionAmount?: string | null
      maximumTransactionAmount?: string | null
      ecommerceBasisPoints?: string | null
      manuallyKeyedCnpMotoBasisPoints?: string | null
      manuallyKeyedCpImprintBasisPoints?: string | null
      cardPresentBasisPoints?: string | null
      annualAmexVolumeExceeds?: boolean | null
      esaNumber?: string | null
      seasonalBusiness?: boolean | null
      seasonalBusinessMonths?: string[] | null
    }
    fulfillmentDetails?: {
      fulfillmentMethod?: FulfillmentMethod | null
      fulfillmentMethodDescription?: string | null
    }
  }

  if (
    root.businessStructureDetails?.businessType === BusinessType.Other &&
    !root.businessStructureDetails?.businessTypeDescription?.trim()
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'businessTypeDescriptionRequired',
      path: ['businessStructureDetails', 'businessTypeDescription']
    })
  }

  const businessType = root.businessStructureDetails?.businessType
  if (businessType && businessType !== BusinessType.Internet) {
    const rawLocations = root.businessProcessingInfo?.numberOfPhysicalLocations
    const locations =
      rawLocations === '' || rawLocations == null ? NaN : Number(rawLocations)
    if (Number.isNaN(locations)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['businessProcessingInfo', 'numberOfPhysicalLocations']
      })
    } else if (locations < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'physicalLocationsMinOne',
        path: ['businessProcessingInfo', 'numberOfPhysicalLocations']
      })
    }
  }

  if (
    root.businessProcessingInfo?.hasAcceptedCreditCardsBefore &&
    !root.businessProcessingInfo?.hasAcceptedCcBeforeDescription?.trim()
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'ccAcceptanceDescriptionRequired',
      path: ['businessProcessingInfo', 'hasAcceptedCcBeforeDescription']
    })
  }

  if (
    root.businessProcessingInfo?.hasExistingPaycomMerchant &&
    !root.businessProcessingInfo?.existingPaycomMerchantId?.trim()
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'existingMerchantIdRequired',
      path: ['businessProcessingInfo', 'existingPaycomMerchantId']
    })
  }

  const ba = root.businessActivity ?? {}
  const monthly = Number(ba.monthlyCardVolumeAmount)
  const maxMonthly = Number(ba.maximumMonthlyCardVolumeAmount)
  if (
    Number.isFinite(monthly) &&
    Number.isFinite(maxMonthly) &&
    maxMonthly < monthly
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'highMonthlyVolumeMin',
      path: ['businessActivity', 'maximumMonthlyCardVolumeAmount']
    })
  }

  const avgTicket = Number(ba.averageTransactionAmount)
  const maxTicket = Number(ba.maximumTransactionAmount)
  if (
    Number.isFinite(avgTicket) &&
    Number.isFinite(maxTicket) &&
    maxTicket < avgTicket
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'highTicketMin',
      path: ['businessActivity', 'maximumTransactionAmount']
    })
  }

  const basisPointsParts = [
    ba.ecommerceBasisPoints,
    ba.manuallyKeyedCnpMotoBasisPoints,
    ba.manuallyKeyedCpImprintBasisPoints,
    ba.cardPresentBasisPoints
  ]
  if (basisPointsParts.every(v => v != null && v !== '')) {
    const total = basisPointsParts.reduce(
      (acc: number, v) => acc + Number(v),
      0
    )
    if (total !== 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'processingMethodsTotal',
        path: ['businessActivity', 'cardPresentBasisPoints']
      })
    }
  }

  if (ba.annualAmexVolumeExceeds) {
    const esaNumber = ba.esaNumber?.trim() ?? ''
    if (!/^\d{9,10}$/.test(esaNumber)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'esaNumberInvalid',
        path: ['businessActivity', 'esaNumber']
      })
    }
  }

  if (
    ba.seasonalBusiness &&
    (!ba.seasonalBusinessMonths || ba.seasonalBusinessMonths.length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'seasonalMonthsRequired',
      path: ['businessActivity', 'seasonalBusinessMonths']
    })
  }

  if (
    root.fulfillmentDetails?.fulfillmentMethod ===
      FulfillmentMethod.ManagedByThirdParty &&
    !root.fulfillmentDetails?.fulfillmentMethodDescription?.trim()
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'fulfillmentDescriptionRequired',
      path: ['fulfillmentDetails', 'fulfillmentMethodDescription']
    })
  }
}

export const businessActivitySchema = z.preprocess(
  (val: unknown) => {
    const data = val as Record<string, Record<string, unknown>> | undefined
    const businessType = data?.businessStructureDetails?.businessType as
      | BusinessType
      | null
      | undefined
    const numberOfPhysicalLocations =
      data?.businessProcessingInfo?.numberOfPhysicalLocations

    if (
      businessType &&
      businessType !== BusinessType.Internet &&
      (numberOfPhysicalLocations === '' || numberOfPhysicalLocations == null)
    ) {
      return {
        ...data,
        businessProcessingInfo: {
          ...data?.businessProcessingInfo,
          numberOfPhysicalLocations: NaN
        }
      }
    }
    return val
  },
  z.intersection(z.any().superRefine(crossFieldRefinements), baseSchema)
)

export type BusinessActivityForm = z.infer<typeof baseSchema>
